import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { forbiddenCharacters } from '@shared/validators';

/**
 * Saves a blob as a file with the specified filename.
 *
 * @param {Blob} blob - The blob to save
 * @param {string} filename - The name of the file to save
 */
export function saveAs(blob: Blob, filename: string) {
  // Create a temporary anchor element
  const link = document.createElement('a');

  // Create a URL for the blob
  const url = window.URL.createObjectURL(blob);

  // Set anchor properties
  link.href = url;

  // Make sure our filename is safe for all OS
  link.download = sanitizeFilename(filename);

  // Append to document, click, and cleanup
  document.body.appendChild(link);
  link.click();

  // Cleanup
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
}

/**
 * Extracts the blob and filename from the HTTP response.
 * @param response The HTTP response containing the file data.
 * @returns An object containing the blob and filename.
 * @throws Will throw an error if the filename is not found or the response is invalid.
 * @example
 */
export function extractBlobAndFilename(response: HttpResponse<Blob>) {
  if (!response.body) {
    throw 'Blob not found in response';
  }

  const filename = getFilenameFromContentDispositionHeader(response.headers);

  if (!filename) {
    throw 'No filename found';
  }

  return { blob: response.body, filename };
}

/**
 * Get the filename from the Content-Disposition header.
 * @param headers The HTTP headers from the response.
 * @returns The filename if found, otherwise null.
 */
export function getFilenameFromContentDispositionHeader(headers: HttpHeaders) {
  const contentDisposition =
    headers.get('content-disposition') || headers.get('Content-Disposition');
  if (!contentDisposition) {
    return null;
  }

  const matches = contentDisposition.match(/filename="?([^";]+)"?/);
  if (matches && matches[1]) {
    return matches[1];
  }
  return null;
}

export function openFileInNewTab(blob: Blob) {
  const blobUrl = URL.createObjectURL(blob);
  const newTab = window.open(blobUrl, '_blank');

  // Clean up the blob URL after a delay to ensure it's loaded
  if (newTab) {
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  } else {
    // If popup was blocked, clean up immediately
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Sanitizes a file name to make it safe for Windows, Linux, and Mac users.
 * Ensures that file names do not contain any invalid/forbidden characters.
 *
 * @see {@link https://learn.microsoft.com/en-us/windows/win32/fileio/naming-a-file Win32 file name rules}
 *
 * @param {string} filename - The original file name
 * @param {string} replaceInvalidCharactersWith - The character to replace invalid characters with
 * @returns {string} A sanitized file name
 */
export function sanitizeFilename(
  filename: string,
  replaceInvalidCharactersWith: string = '_',
): string {
  // Remove quotes first
  let sanitized = filename.replace(/['"]/g, '');

  // Replace invalid characters with the specified character.
  // The following reserved characters:
  //     < (less than)
  //     > (greater than)
  //     : (colon)
  //     " (double quote) - already removed above
  //     / (forward slash)
  //     \ (backslash)
  //     | (vertical bar or pipe)
  //     ? (question mark)
  //     * (asterisk)

  // Replace invalid characters with the specified replacement character
  sanitized = sanitized.replace(/[<>:"/\\|?*]/g, replaceInvalidCharactersWith);

  // Remove trailing spaces and periods
  sanitized = sanitized.replace(/[ .]+$/, '');

  // Replace any forbidden (business driven requirements) characters
  sanitized = sanitized.replace(
    forbiddenCharactersRegex,
    replaceInvalidCharactersWith,
  );

  // Ensure the name is not a reserved Windows name
  const reservedNames = new Set([
    'CON',
    'PRN',
    'AUX',
    'NUL',
    'COM1',
    'COM2',
    'COM3',
    'COM4',
    'COM5',
    'COM6',
    'COM7',
    'COM8',
    'COM9',
    'LPT1',
    'LPT2',
    'LPT3',
    'LPT4',
    'LPT5',
    'LPT6',
    'LPT7',
    'LPT8',
    'LPT9',
  ]);

  if (reservedNames.has(sanitized.toUpperCase())) {
    sanitized = `_${sanitized}`;
  }

  return sanitized;
}

function escapeForRegex(char: string): string {
  return char.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
}

const forbiddenCharactersRegex = new RegExp(
  `[${forbiddenCharacters.map(escapeForRegex).join('')}]`,
  'gu',
);
